<template>
    <header ref="header" class="bg-white-light text-black backdrop-blur-md fixed top-[0px] w-full z-30 border-b border-gray-300 left-[0%]" :class="open ? 'bg-white-light' : ''">
        <div ref="slot">
            <slot />
        </div>

        <div
            ref="header_container"
            :class="[getApplicationType() === 'frontend' ? 'mx-auto' : '', 'transition-height duration-150 px-2 md:px-8 ease-in-out flex flex-row align-center']"
        >
            <div class="flex justify-between items-center w-full gap-2 md:gap-12">
                <div v-if="getApplicationType() === 'frontend'" ref="logo" class="block w-[110px] md:w-[150px] shrink-0 cursor-pointer" @click="logoClick()">
                    <!-- Usual Logo -->
                    <img v-if="region === 'de'" class="w-full h-auto" :src="$asset('/hkcm/images/logo-on-light-background.svg', 'public')" alt="HKCM Logo" title="HKCM Logo" />
                    <!-- Global Logo -->
                    <img v-else class="w-full h-auto" :src="$asset('/hkcm/images/logo-hkcm-global-2.svg', 'public')" alt="HKCM Global Logo" title="HKCM Global Logo" />
                </div>

                <div v-if="getApplicationType() === 'frontend' && !isSlimHeaderRoute" ref="menu" class="hidden xl:flex w-full items-center gap-6 h-full relative">
                    <div v-for="(menuEntry, index) in menuEntries" :key="menuEntry.url" class="relative group">
                        <router-link class="h-full flex items-center small text-sm" :to="menuEntry.url">
                            <img
                                v-if="index === 1"
                                class="min-w-32 w-32 pr-2"
                                :src="$asset('/hkcm/images/hkcm-news.png', 'public')"
                                :alt="region === 'de' ? 'HKCM News Logo' : 'HKCM Global News Logo'"
                                :title="region === 'de' ? 'HKCM News Logo' : 'HKCM Global News Logo'"
                            />
                            <div v-else>{{ $t(menuEntry.phrase_key) }}</div>
                        </router-link>
                        <!-- NEU-Tag: New-Icon for new Navigation entries  -->
                        <!-- <img v-if="index === 0" class="absolute right-0 top-[95%] w-12" :src="$asset('/hkcm/images/neu.png', 'public')" alt="HKCM Logo" title="HKCM Logo"/> -->
                    </div>
                </div>

                <div
                    v-show="isAnySidebarOpen && $store.getters['settings/getCurrentOpenSidebarName']() !== 'backend_sidebar'"
                    ref="overlay"
                    class="xl:hidden absolute top-0 bottom-0 left-0 right-0 bg-black/20 z-50 h-app w-full"
                    @click.stop="toggle()"
                />

                <div
                    ref="sidebar"
                    :class="[
                        $store.getters['settings/getSidebarStateByName']('menu_sidebar') ? 'translate-x-0' : '-translate-x-full',
                        getBackendType() === 'dashboard' ? 'bg-blue-dark' : 'bg-white-light'
                    ]"
                    class="overflow-scroll xl:hidden text-black ease-in-out transition-translate duration-300 fixed bottom-0 left-0 w-full md:w-[500px] z-50 transform shadow-lg h-app"
                >
                    <!-- Mobile Dashboard Menu -->
                    <div v-if="getBackendType() === 'dashboard'" class="h-full xl:flex flex-col justify-between relative text-white transition-all ease-in-out duration-500">
                        <div
                            class="absolute top-0 right-0 transform p-2 cursor-pointer xl:block hidden"
                            @click.stop="$store.commit('settings/toggleBackendSidebar', { name: 'backend_sidebar' })"
                        >
                            <Icon
                                type="Toggle"
                                viewBox="0 0 13 13"
                                width="15"
                                height="15"
                                :class="$store.getters['settings/getBackendSidebarStateByName']('backend_sidebar') ? '' : 'rotate-180'"
                                class="fill-current"
                            />
                        </div>

                        <div class="flex flex-col h-full">
                            <div class="flex justify-center items-center px-8" style="min-height: var(--headerHeight); margin-top: var(--slotHeight)">
                                <router-link to="/" class="block w-[110px] md:w-[150px] shrink-0 cursor-pointer">
                                    <img
                                        v-if="!$store.getters['settings/getBackendSidebarStateByName']('backend_sidebar')"
                                        class="w-full h-auto"
                                        :src="$asset('/hkcm/images/logo-on-dark-background.svg', 'public')"
                                        alt="HKCM Logo"
                                        title="HKCM Logo"
                                    />
                                    <img
                                        v-if="$store.getters['settings/getBackendSidebarStateByName']('backend_sidebar')"
                                        class="w-full h-[44px]"
                                        :src="$asset('/hkcm/images/logo-small-on-dark-background.svg', 'public')"
                                        alt="HKCM Logo"
                                        title="HKCM Logo"
                                    />
                                </router-link>
                            </div>

                            <div v-if="isSmallerViewpoint" class="p-8 h-full flex items-center">
                                <div class="w-full text-xl">
                                    <SidebarNavigationItems />
                                </div>
                            </div>

                            <div :class="$store.getters['settings/getBackendSidebarStateByName']('backend_sidebar') ? '' : 'hidden'" class="p-8 h-full flex items-center">
                                <div class="rounded-xl bg-[#011128] p-4 w-full">
                                    <img class="relative bottom-[80px] block m-auto" :src="$asset('/hkcm/images/HKCMAnalysen.svg', 'public')" />
                                    <p class="text-center mt-[-50px]">
                                        <span class="font-bold pb-2 block">{{ $t('discover_all_markets_heading') }}</span
                                        >{{ $t('discover_all_markets') }}
                                    </p>
                                    <router-link to="/produkte" class="mainBlue-BG px-6 py-3 rounded-xl w-max block m-auto mt-4 hover:scale-105 duration-300">
                                        <span class="">{{ $t('all_markets') }}</span>
                                    </router-link>
                                </div>
                            </div>
                            <div class="w-full my-5">
                                <button
                                    v-if="isLoggedIn"
                                    class="rounded-md bg-gray-light text-black flex items-center gap-3 p-2 self-end m-auto"
                                    @click="$store.dispatch('user/logout')"
                                >
                                    <Icon type="Logout" viewBox="0 0 21 21" width="21" height="21" class="fill-none stroke-current shrink-0" />
                                    <span class="whitespace-nowrap truncate">{{ $t('account_logout') }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="getBackendType() === 'profile'" class="h-full flex flex-col">
                        <router-link to="/dashboard" active-class="border-blue bg-blue/20" class="bg-white border-l-4 pl-3 pr-3 py-2 cursor-pointer whitespace-nowrap truncate">
                            {{ $t('plain_dashboard') }}
                        </router-link>
                        <!-- Profile Links -->
                        <div class="my-4">
                            <div class="py-2">
                                <small class="uppercase text-gray-400">
                                    {{ $t('plain_abonnement') }}
                                </small>
                            </div>

                            <div class="flex flex-col gap-2">
                                <router-link
                                    to="/profile/abonnements"
                                    active-class="border-blue bg-blue/20"
                                    class="bg-white border-l-4 pl-3 pr-3 py-2 cursor-pointer whitespace-nowrap truncate"
                                >
                                    {{ $t('plain_abonnements') }}
                                </router-link>
                                <router-link
                                    to="/profile/invoices"
                                    active-class="border-blue bg-blue/20"
                                    class="bg-white border-l-4 pl-3 pr-3 py-2 cursor-pointer whitespace-nowrap truncate"
                                >
                                    {{ $t('invoices') }}
                                </router-link>
                                <router-link
                                    to="/profile/paymentmethods"
                                    active-class="border-blue bg-blue/20"
                                    class="bg-white border-l-4 pl-3 pr-3 py-2 cursor-pointer whitespace-nowrap truncate"
                                >
                                    {{ $t('payment_methods') }}
                                </router-link>
                            </div>
                        </div>
                        <div class="my-4">
                            <div class="py-2">
                                <small class="uppercase text-gray-400">
                                    {{ $t('settings') }}
                                </small>
                            </div>

                            <div class="flex flex-col gap-2">
                                <router-link
                                    to="/profile"
                                    active-class="border-blue bg-blue/20"
                                    class="bg-white border-l-4 pl-3 pr-3 py-2 cursor-pointer whitespace-nowrap truncate"
                                >
                                    {{ $t('nav_profile') }}
                                </router-link>

                                <router-link
                                    to="/profile/change-password"
                                    active-class="border-blue bg-blue/20"
                                    class="bg-white border-l-4 pl-3 pr-3 py-2 cursor-pointer whitespace-nowrap truncate"
                                >
                                    {{ $t('change_password') }}
                                </router-link>
                                <router-link
                                    to="/profile/email"
                                    active-class="border-blue bg-blue/20"
                                    class="bg-white border-l-4 pl-3 pr-3 py-2 cursor-pointer whitespace-nowrap truncate"
                                >
                                    {{ $t('mail_settings') }}
                                </router-link>
                                <button
                                    v-if="isLoggedIn"
                                    class="rounded-md bg-gray-light text-black flex items-center gap-3 p-2 self-center mt-2 sm:mt-5"
                                    @click="$store.dispatch('user/logout')"
                                >
                                    <Icon type="Logout" viewBox="0 0 21 21" width="21" height="21" class="fill-none stroke-current shrink-0" />
                                    <span class="whitespace-nowrap truncate">{{ $t('account_logout') }}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <!-- Profile Links -->
                    <div v-else class="flex flex-col gap-4 p-8">
                        <router-link v-for="menuEntry in visibleMenuEntries" :key="menuEntry.url" class="small" :to="menuEntry.url">
                            {{ $t(menuEntry.phrase_key) }}
                        </router-link>
                        <router-link v-if="region === 'de'" to="/geschenk" class="small flex items-center gap-2">
                            <span>Abo verschenken </span>
                        </router-link>
                    </div>
                </div>

                <div
                    ref="cart"
                    :class="$store.getters['settings/getSidebarStateByName']('cart_sidebar') ? 'translate-x-0' : 'translate-x-full'"
                    class="bg-white-light text-black ease-in-out transition-translate duration-300 fixed bottom-0 right-0 w-full md:w-[500px] z-50 transform shadow-lg h-app"
                >
                    <Cart :open="true" class="p-8 pb-16">
                        <div class="flex flex-row justify-between items-center">
                            <h6 class="font-bold">{{ $t('nav_cart') }}</h6>

                            <span>
                                {{ $t('payment_count_articles', { count: $store.getters['cart/getCount'] }) }}
                            </span>
                        </div>
                    </Cart>
                </div>

                <div class="flex items-center justify-end gap-2 w-full">
                    <div
                        v-if="isAnyPopupOpen === false && !isSlimHeaderRoute"
                        class="hidden xl:flex items-center justify-center text-blue-dark relative cursor-pointer"
                        @click.stop="$router.push('/produkte')"
                    >
                        <div v-if="getApplicationType() !== 'backend'" class="flex items-center gap-4">
                            <Icon type="List" viewBox="0 0 21 17" width="21" height="17" class="shrink-0" />
                            <span class="leading-none hidden xl:inline-block">
                                {{ $t('nav_products') }}
                            </span>

                            <!-- NEU-Tag New-Icon for new Navigation entries -->
                            <!-- <img class="absolute right-0 top-[95%] w-12" :src="$asset('/hkcm/images/neu.png', 'public')" alt="HKCM Logo" title="HKCM Logo"/> -->
                        </div>
                    </div>
                    <!-- "Abo verschenken" Button -->
                    <div
                        v-if="region === 'de' && !isSlimHeaderRoute"
                        class="hidden xl:flex items-center justify-center text-blue-dark relative cursor-pointer border border-gray-400 rounded-lg p-2 hover:bg-blue/20 transition-colors"
                        @click.stop="$router.push('/geschenk')"
                    >
                        <div class="flex items-center gap-2">
                            <i class="fi fi-rr-gift text-xl shrink-0"></i>
                            <span class="leading-none hidden xl:inline-block"> Abo verschenken </span>
                        </div>
                    </div>
                    <CurrencySwitch v-if="!isSlimHeaderRoute" />

                    <!-- Warenkorb Button -->
                    <div
                        v-if="getApplicationType() !== 'backend' && !isSlimHeaderRoute"
                        class="w-[45px] h-[45px] xl:w-auto xl:h-auto flex items-center justify-center text-blue-dark relative cursor-pointer rounded-md p-2 bg-gray-light xl:bg-transparent"
                        @click.stop="$store.commit('settings/toggleSidebar', { name: 'cart_sidebar' })"
                    >
                        <div
                            v-if="$store.getters['cart/getCount'] > 0"
                            class="rounded-xl bg-blue text-white-light text-xxs w-5 h-5 absolute xl:-top-1 xl:-left-1 -top-2 -left-1 flex justify-center items-center leading-[0]"
                        >
                            {{ $store.getters['cart/getCount'] }}
                        </div>

                        <div class="flex items-center gap-2">
                            <Icon type="Basket" viewBox="0 0 16 21" width="21" height="21" class="shrink-0" />
                            <span v-if="isAnyPopupOpen === false" class="leading-none hidden xl:inline-block">
                                {{ $t('nav_cart') }}
                            </span>
                        </div>
                    </div>

                    <div
                        v-if="(isAnyPopupOpen || isAnySidebarOpen) && !isSlimHeaderRoute"
                        class="hidden xl:flex items-center justify-center text-blue-dark relative cursor-pointer"
                    >
                        <div class="flex items-center gap-4">
                            <Icon type="Close" viewBox="0 0 26 26" width="21" height="21" class="shrink-0" />
                        </div>
                    </div>

                    <div v-if="getApplicationType() === 'backend'" class="flex flex-row space-x-1.5 items-center text-black">
                        <div @click="navigateToTutorial">
                            <i
                                class="fi fi-rr-question whitespace-nowrap border-0 bg-white p-3 rounded-xl flex items-center object-contain relative group text-lg z-10 duration-300 hover:shadow-md hover:bg-blue hover:text-white cursor-pointer"
                                style="width: 48px; height: 47px"
                            ></i>
                        </div>

                        <div
                            v-if="internalUser && $route.path === '/dashboard'"
                            :class="{ 'backgroundlightingActiv text-white rounded-xl': editMode }"
                            class="group whitespace-nowrap border-0 bg-white p-3 rounded-xl flex items-center justify-center relative duration-300 group-hover:shadow-md cursor-pointer hover:bg-blue w-12 h-12"
                        >
                            <i class="fi fi fi-rs-magic-wand text-lg pt-2 relative z-10 duration-300 group-hover:text-white" @click.stop="toggleEditMode" />
                        </div>
                        <router-link to="/produkte" class="relative inline-block">
                            <i
                                class="fi-rr-shopping-cart-add fi whitespace-nowrap border-0 bg-white p-3 rounded-xl flex items-center object-contain text-lg z-10 duration-300 hover:shadow-md hover:bg-blue hover:text-white cursor-pointer"
                                style="width: 48px; height: 47px"
                            ></i>

                            <div
                                v-if="$store.getters['cart/getCount'] > 0"
                                class="rounded-xl bg-blue text-white text-xxs w-5 h-5 absolute -top-2 -left-1 flex justify-center items-center leading-[0]"
                            >
                                {{ $store.getters['cart/getCount'] }}
                            </div>
                        </router-link>
                        <div v-if="region === 'de'" @click="navigateToSurvey()">
                            <i
                                class="fi fi-rs-feedback-hand whitespace-nowrap border-0 bg-white p-3 rounded-xl flex items-center object-contain group text-xl relative z-10 duration-300 hover:shadow-md hover:bg-blue hover:text-white cursor-pointer"
                                style="width: 48px; height: 47px"
                            ></i>
                        </div>
                    </div>

                    <div v-if="isAnyPopupOpen === false" class="hidden xl:block relative tourStep-6">
                        <div>
                            <button
                                v-if="isLoggedIn"
                                :class="userDropdownOpen ? 'rounded-br-xl rounded-bl-xl rounded-t-2xl ' : ''"
                                class="bg-white rounded-xl px-8 py-3 flex flex-row gap-2 items-center hover:scale-105 duration-300 hover:shadow-md cursor-pointer"
                                @click.stop="userDropdownOpen = !userDropdownOpen"
                            >
                                <Icon type="UserRound" viewBox="0 0 21 21" width="21" height="21" class="shrink-0 fill-current" />
                                {{ user.name }}
                            </button>
                            <router-link v-else class="btn primary flex flex-row gap-2 items-center px-3" to="/login">
                                <Icon type="UserRound" viewBox="0 0 21 21" width="21" height="21" class="shrink-0 fill-current" />
                                {{ $t('account_login') }}
                            </router-link>
                        </div>
                        <div
                            v-show="userDropdownOpen"
                            class="bg-white text-xs text-dashboard-primary-grey shadow-md absolute left-0 z-[51] w-full rounded-xl border border-white pt-2 pb-2 mt-1 dropdown-menu px-2"
                        >
                            <router-link
                                v-if="isLoggedIn"
                                to="/dashboard"
                                class="text-black flex items-center justify-between gap-3 w-full p-2 px-3 hover:bg-blue-light rounded-lg"
                                @click.stop="userDropdownOpen = !userDropdownOpen"
                            >
                                <span class="whitespace-nowrap truncate">{{ $t('nav_dashboard') }}</span>
                            </router-link>
                            <router-link
                                v-if="isLoggedIn"
                                to="/profile"
                                class="text-black flex items-center justify-between gap-3 w-full p-2 px-3 hover:bg-blue-light rounded-lg"
                                @click.stop="userDropdownOpen = !userDropdownOpen"
                            >
                                <span class="whitespace-nowrap truncate">{{ $t('nav_profile') }}</span>
                            </router-link>
                            <button
                                v-if="isLoggedIn"
                                class="text-black flex items-center justify-between gap-3 w-full p-2 px-3 hover:bg-blue-light rounded-lg"
                                @click="[$store.dispatch('user/logout'), (userDropdownOpen = !userDropdownOpen)]"
                            >
                                <span class="whitespace-nowrap truncate">{{ $t('account_logout') }}</span>
                                <Icon type="Logout" viewBox="0 0 21 21" width="21" height="21" class="fill-none stroke-current shrink-0" />
                            </button>
                        </div>
                    </div>

                    <div
                        v-if="isAnyPopupOpen === false && !isSlimHeaderRoute"
                        class="text-blue-dark w-[45px] h-[45px] xl:w-auto xl:h-auto flex items-center justify-center bg-gray-light rounded-md xl:hidden outline-none focus:outline-none appearance-none relative hover:scale-105 duration-300 hover:shadow-md cursor-pointer"
                        @click.stop="isLoggedIn ? $router.push('/profile') : $router.push('/login')"
                    >
                        <Icon type="UserRound" viewBox="0 0 21 21" width="21" height="21" class="shrink-0" />
                    </div>

                    <button
                        v-if="!isSlimHeaderRoute"
                        aria-label="Menu"
                        class="w-[45px] h-[45px] xl:w-auto xl:h-auto flex items-center justify-center bg-gray-light rounded-md xl:hidden outline-none focus:outline-none appearance-none relative hover:scale-105 duration-300 hover:shadow-md cursor-pointer"
                        @click.stop="toggle()"
                    >
                        <div class="relative w-6 h-8 flex justify-center items-center">
                            <span
                                :class="
                                    $store.getters['settings/getSidebarStateByName']('menu_sidebar') || isAnyPopupOpen || isAnySidebarOpen ? 'rotate-45 w-6' : 'w-6 -translate-y-2'
                                "
                                class="block absolute right-0 h-0.5 transform transition duration-500 ease-in-out bg-blue-dark"
                            >
                            </span>
                            <span
                                :class="$store.getters['settings/getSidebarStateByName']('menu_sidebar') || isAnyPopupOpen || isAnySidebarOpen ? 'opacity-0 w-6' : 'w-4'"
                                class="block absolute right-0 h-0.5 transform transition duration-500 ease-in-out bg-blue-dark"
                            >
                            </span>
                            <span
                                :class="
                                    $store.getters['settings/getSidebarStateByName']('menu_sidebar') || isAnyPopupOpen || isAnySidebarOpen ? '-rotate-45 w-6' : 'w-2 translate-y-2'
                                "
                                class="block absolute right-0 h-0.5 transform transition duration-500 ease-in-out bg-blue-dark"
                            >
                            </span>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    </header>

    <template v-if="getBackendType() === 'dashboard'">
        <ExpiredPaymentMethodModal />
    </template>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ExpiredPaymentMethodModal from '../customer/ExpiredPaymentMethodModal.vue';
import SidebarNavigationItems from '../customer/SidebarNavigationItems.vue';
import Icon from '../miscellaneous/Icon.vue';
import CurrencySwitch from './CurrencySwitch.vue';

export default {
    components: { SidebarNavigationItems, ExpiredPaymentMethodModal, CurrencySwitch, Icon },

    data: () => ({
        open: false,
        cartOpen: false,
        transformed: false,
        menuEntries: [
            {
                url: '/charts',
                phrase_key: 'nav_our_charts'
            },
            {
                url: '/hkcmnews',
                phrase_key: 'nav_hkmc_news'
            },
            {
                url: '/howto#faqs',
                phrase_key: 'FAQs'
            },
            {
                url: '/aboutus',
                phrase_key: 'nav_our_team'
            }
        ],
        extraMenuEntriesMobile: [
            {
                url: '/dashboard',
                phrase_key: 'nav_dashboard',
                login_required: true
            },
            {
                url: '/produkte',
                phrase_key: 'nav_products'
            }
        ],
        headerHeight: 80,
        headerHeightScrolled: 80,
        headerHeightCurrent: 80,
        slotHeight: 0,
        containerScrollTop: 0,
        appHeight: 0,
        userDropdownOpen: false,
        notificationDropdownOpen: false,
        contentSidebarOpen: false,
        editMode: false,
        isHovered: false,
        internalUser: null,
        viewportWidth: window.innerWidth
    }),
    computed: {
        isLoggedIn: function () {
            return this.$store.getters['user/isLoggedIn'];
        },
        ...mapGetters('user', {
            user: 'get'
        }),
        ...mapGetters('settings', {
            isAnyPopupOpen: 'isAnyPopupOpen',
            getApplicationType: 'getApplicationType',
            getHeaderName: 'getHeaderName',
            isAnySidebarOpen: 'isAnySidebarOpen',
            getBackendType: 'getBackendType'
        }),
        region() {
            return window.meta?.region_configuration?.region || 'de';
        },
        visibleMenuEntries() {
            const mobile = Array.isArray(this.extraMenuEntriesMobile) ? this.extraMenuEntriesMobile : [];
            const desktop = Array.isArray(this.menuEntries) ? this.menuEntries : [];

            return [...mobile, ...desktop].filter((m) => {
                const requiresLogin = Boolean(m?.login_required);

                return requiresLogin ? this.isLoggedIn : true;
            });
        },
        isSmallerViewpoint() {
            return this.viewportWidth <= 1280;
        },
        isSlimHeaderRoute() {
            return Boolean(this.$route?.meta?.slimHeader);
        }
    },

    async mounted() {
        if (this.$store.getters['user/isLoggedIn']) {
            await this.$store.dispatch('user/user').then((response) => {
                const betaUser =
                    response.data?.email.includes('@hkcmanagement.de') || response.data?.email.includes('@medienakzent.de') || response.data?.email.includes('heiland.marc@');
                this.internalUser = betaUser;
            });
        }

        window.addEventListener('scroll', this.animateHeader);
        window.addEventListener('resize', this.animateHeader);

        this.animateHeader();

        this.editMode = window.editMode;

        // Event-Listener hinzufügen
        window.addEventListener('editModeChange', this.updateEditMode);
        document.addEventListener('click', this.closeDropdown);
        this._resizeHandler = () => {
            this.viewportWidth = window.innerWidth;
        };
        window.addEventListener('resize', this._resizeHandler);
    },

    beforeUnmount() {
        // Event-Listener entfernen
        window.removeEventListener('editModeChange', this.updateEditMode);
        document.removeEventListener('click', this.closeDropdown);
        window.removeEventListener('resize', this._resizeHandler);
    },

    unmounted() {
        window.removeEventListener('scroll');
        window.removeEventListener('resize');
    },

    methods: {
        navigateToSurvey() {
            window.open('https://w04qocq6ivc.typeform.com/to/f712bBBv', '_blank');
        },
        navigateToTutorial() {
            // Navigiere zur Dashboard-Seite mit dem Query-Parameter 'neukunde=true'
            this.$router.push({ path: '/dashboard', query: { neukunde: 'true' } });
        },
        closeDropdown(event) {
            // Überprüfen, ob der Klick außerhalb des Dropdowns ist

            this.userDropdownOpen = false;
            this.notificationDropdownOpen = false;
        },

        toggleEditMode() {
            // Ändere den Edit-Modus
            this.editMode = !this.editMode;

            // Ändere auch die window Variable
            window.editMode = this.editMode;

            // Event auslösen
            const event = new Event('editModeChange');
            window.dispatchEvent(event);
        },
        updateEditMode() {
            // Aktualisiere die Daten, wenn sich die Window-Variable ändert
            this.editMode = window.editMode;
        },

        goto(id) {
            document.getElementById(id).scrollIntoView({
                behavior: 'smooth',
                inline: 'start'
            });
        },

        logoClick() {
            this.$router.push('/');
            this.$store.commit('settings/closeAllPopups');

            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        },

        toggle() {
            if (this.isAnyPopupOpen || this.isAnySidebarOpen) {
                this.$store.commit('settings/closeAllPopups');
                this.$store.commit('settings/closeAllSidebars');
            } else {
                this.$store.commit('settings/toggleSidebar', { name: 'menu_sidebar' });
            }
        },

        animateHeader() {
            this.containerScrollTop = window.scrollY;

            if (this.containerScrollTop <= 0) {
                this.headerHeightCurrent = this.headerHeight;
                this.slotHeight = this.$refs.slot?.offsetHeight ? this.$refs.slot?.offsetHeight : 0;
                this.appHeight = window.innerHeight - (this.headerHeightCurrent + this.slotHeight);
                this.transformed = false;

                if (this.$refs.header_container) {
                    this.$refs.header_container.style.height = this.headerHeight + 'px';
                    this.$refs.sidebar.style.top = this.headerHeightCurrent + this.slotHeight + 'px';
                    this.$refs.cart.style.top = this.headerHeightCurrent + this.slotHeight + 'px';
                    this.$refs.overlay.style.top = this.headerHeightCurrent + this.slotHeight + 'px';
                }

                setProperties(this.appHeight, this.slotHeight, this.headerHeight, this.headerHeightCurrent);
            } else if (this.containerScrollTop > this.headerHeight && !this.transformed) {
                this.headerHeightCurrent = this.headerHeightScrolled;
                this.slotHeight = this.$refs.slot?.offsetHeight ? this.$refs.slot?.offsetHeight : 0;
                this.appHeight = window.innerHeight - (this.headerHeightCurrent + this.slotHeight);
                this.transformed = true;

                if (this.$refs.header_container) {
                    this.$refs.header_container.style.height = this.headerHeightScrolled + 'px';
                    this.$refs.sidebar.style.top = this.headerHeightCurrent + this.slotHeight + 'px';
                    this.$refs.cart.style.top = this.headerHeightCurrent + this.slotHeight + 'px';
                    this.$refs.overlay.style.top = this.headerHeightCurrent + this.slotHeight + 'px';
                }

                setProperties(this.appHeight, this.slotHeight, this.headerHeight, this.headerHeightCurrent);
            }

            function setProperties(appHeight, slotHeight, headerHeight, headerHeightCurrent) {
                document.documentElement.style.setProperty('--appHeight', `${appHeight}px`);
                document.documentElement.style.setProperty('--slotHeight', `${slotHeight}px`);
                document.documentElement.style.setProperty('--headerHeight', `${headerHeight + slotHeight}px`);
                document.documentElement.style.setProperty('--headerHeightCurrent', `${headerHeightCurrent + slotHeight}px`);
            }
        },

        ...mapActions('user', ['logout'])
    }
};
</script>

<style scoped>
.activeMenuItem {
    background-color: #353cee;
    color: #ffffff;
}

.background-animate {
    background-size: 400%;
    animation: AnimationName 5s ease infinite;
}

@keyframes AnimationName {
    0%,
    100% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
}

.active {
    color: green;
}

.backgroundlightingActiv {
    position: relative;
    border-radius: 9999px; /* Ensure it's fully rounded */
}

.backgroundlightingActiv:before,
.backgroundlightingActiv:after {
    content: '';
    position: absolute;
    inset: 0; /* Cover the entire parent */
    background: linear-gradient(45deg, #031837, #363cd2, #031837);
    background-size: 400%;
    z-index: 0;
    animation: steam 20s linear infinite;
    border-radius: 9999px; /* Ensure it's fully rounded */
}

@keyframes steam {
    0% {
        background-position: 0 0;
    }
    50% {
        background-position: 400% 0;
    }
    100% {
        background-position: 0 0;
    }
}

.backgroundlightingActiv:after {
    filter: blur(20px);
}
</style>
